import { debugLog, RELAXATION_LEVELS, MATCHMAKING_CONFIG } from './matchmakingConfig.js';
import { isSinglesFormat } from './competitiveMatchFormat.js';
import { getBestSinglesCourtAssignments } from './singlesCourtEngine.js';
import { buildEligiblePool, getUnitAverageGames } from './poolBuilder.js';
import { 
  evaluateWithProgressiveRelaxation, 
  evaluateWithNormalizedRelaxation,
  evaluateWithNormalizedRelaxationSingles,
  runPoolExpansionRelaxationGate,
} from './relaxationOrchestrator.js';
import {
  getUnitPlayers,
  getUnitSize,
  getTotalPlayerCount,
  expandUnitsToPlayers,
  UNIT_TYPES,
} from './unitBuilder.js';
import { shapePoolForVariety, SATURATION_HIGH_THRESHOLD } from './varietyShaping.js';
import { DEFAULT_RELAXATION_CONFIG } from './normalizedRelaxation.js';
import { computeWaitGuaranteeThreshold, isSessionDebtModelEnabled, sortPoolUnitsBySessionDebt } from './sessionDebt.js';
import {
  isScoringFirstMatchmakingEnabled,
  computeScoringFirstSaturation,
} from './scoringFirstMatchmaking.js';

// Import feature flag helpers (doubles relaxation only; singles does not use this for path selection)
import { shouldUseNormalizedScoring } from './scoringIntegration.js';
import { computeGameTypeBudget } from './gameTypeBudget.js';

/**
 * Wrapper to choose between legacy and normalized relaxation
 * Based on feature flag, routes to appropriate evaluation function
 */
function evaluateWithRelaxation(...args) {
  const useNormalized = shouldUseNormalizedScoring();
  
  if (useNormalized) {
    debugLog('🎯 Using NEW normalized relaxation system (9 levels)');
    return evaluateWithNormalizedRelaxation(...args);
  } else {
    debugLog('🎯 Using LEGACY relaxation system (2 levels)');
    return evaluateWithProgressiveRelaxation(...args);
  }
}

/**
 * Partnerships where both partners appear in the given player id list.
 */
function filterPartnershipsInPool(partnerships, playerIds) {
  const poolSet = new Set(playerIds);
  return (partnerships || []).filter((p) => {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    return p1 && p2 && poolSet.has(p1) && poolSet.has(p2);
  });
}

/**
 * Build pool → greedy → relaxation for one court assignment attempt.
 * @returns {{ relaxationResult: object, poolRelaxationDetails: string[], varietyMetrics: object, eligiblePoolResult: object }}
 */
function evaluateCourtMatchForPlayers(
  playerIds,
  {
    forceRelaxCooldown = false,
    phaseBounds = null,
    playersById,
    partnerships,
    scheduledGames,
    playerWaitMetrics,
    gameTypeRestriction,
    numberOfCourts,
    matchPreferencesEnabled,
    sessionState,
    playerCategories,
    relaxationConfig = null,
    courtGameTypeHint = null,
    partnershipPoolPlayerIds = null,
    fullWaitlistPlayerIds = null,
  },
) {
  // Recompute per evaluation pass — pool expansion may widen playerIds after
  // partnerships were filtered for the initial narrow subsection.
  const prefsEnabled = matchPreferencesEnabled !== false && sessionState?.matchPreferencesEnabled !== false;
  const cooldownEnabled = sessionState?.cooldownEnabled === true;
  const completePartnerships = filterPartnershipsInPool(partnerships, playerIds);
  const allSessionPartnerships = partnerships || [];
  const eligiblePoolResult = buildEligiblePool(
    playerIds,
    playersById,
    completePartnerships,
    scheduledGames,
    null,
    playerWaitMetrics,
    forceRelaxCooldown,
    gameTypeRestriction,
    numberOfCourts,
    4,
    prefsEnabled,
    cooldownEnabled,
    sessionState,
  );

  const {
    pool: eligiblePoolUnits,
    minGames: sessionMinGames,
    relaxationDetails: poolRelaxationDetails = [],
  } = eligiblePoolResult;

  const totalPlayers = getTotalPlayerCount(eligiblePoolUnits);
  if (totalPlayers < 4) {
    return { relaxationResult: { result: null }, poolRelaxationDetails, varietyMetrics: null, eligiblePoolResult };
  }

  const allPlayerIds = eligiblePoolUnits.flatMap((unit) => getUnitPlayers(unit));
  const gamesPlayed = allPlayerIds.map((id) => {
    const player = playersById[id];
    return player?.effectiveGamesPlayed !== undefined
      ? player.effectiveGamesPlayed
      : (player?.gamesPlayedThisSession || 0);
  });
  const gamesAverage = gamesPlayed.length > 0
    ? gamesPlayed.reduce((sum, g) => sum + g, 0) / gamesPlayed.length
    : 0;

  const varietyFlag = MATCHMAKING_CONFIG.featureFlags.varietyAwarePoolSelection;
  const varietyConfig = MATCHMAKING_CONFIG.variety || {};
  const scoringFirst = isScoringFirstMatchmakingEnabled();

  let poolForGreedy = eligiblePoolUnits;
  let poolResultForEval = eligiblePoolResult;
  let shapingResult = null;
  let sessionStateForEval = sessionState;
  let greedyPlayerIds = expandUnitsToPlayers(eligiblePoolUnits);

  if (scoringFirst) {
    const saturation = computeScoringFirstSaturation(
      sessionState,
      allPlayerIds,
      completePartnerships,
    );
    sessionStateForEval = { ...sessionState, varietySaturation: saturation };
  } else if (varietyFlag) {
    shapingResult = shapePoolForVariety(
      eligiblePoolResult,
      sessionState,
      completePartnerships,
      playerIds,
      playersById,
      completePartnerships,
      varietyConfig,
      false,
      playerWaitMetrics,
      sessionState?.sessionPlayerStatus || null,
    );
    poolForGreedy = shapingResult.shapedPool;
    poolResultForEval = {
      ...eligiblePoolResult,
      pool: shapingResult.shapedPool,
      varietyAwareMode: true,
    };
  } else if (isSessionDebtModelEnabled()) {
    const sortedPool = sortPoolUnitsBySessionDebt(
      eligiblePoolUnits,
      playersById,
      playerWaitMetrics,
      sessionMinGames,
      sessionState,
      playerIds,
    );
    poolForGreedy = sortedPool;
    poolResultForEval = { ...eligiblePoolResult, pool: sortedPool };
  }

  if (!scoringFirst) {
    const greedySelectedUnits = greedySelectHighPriority(
      poolForGreedy,
      playersById,
      gamesAverage,
      playerWaitMetrics,
      completePartnerships,
      sessionMinGames,
    );
    greedyPlayerIds = greedySelectedUnits.flatMap((unit) => getUnitPlayers(unit));
  }

  if (!scoringFirst && shapingResult) {
    sessionStateForEval = { ...sessionState, varietySaturation: shapingResult.saturation };
  } else if (!scoringFirst) {
    sessionStateForEval = sessionState;
  }

  let relaxationResult = evaluateWithRelaxation(
    poolResultForEval,
    playersById,
    allSessionPartnerships,
    sessionStateForEval,
    greedyPlayerIds,
    scheduledGames,
    gameTypeRestriction,
    playerCategories,
    playerIds,
    relaxationConfig,
    phaseBounds,
    courtGameTypeHint,
    partnershipPoolPlayerIds,
  );

  if (
    !scoringFirst
    && !relaxationResult.result
    && greedyPlayerIds.length < expandUnitsToPlayers(poolResultForEval.pool).length
  ) {
    const allEligiblePlayerIds = expandUnitsToPlayers(poolResultForEval.pool);
    relaxationResult = evaluateWithRelaxation(
      poolResultForEval,
      playersById,
      allSessionPartnerships,
      sessionStateForEval,
      allEligiblePlayerIds,
      scheduledGames,
      gameTypeRestriction,
      playerCategories,
      playerIds,
      relaxationConfig,
      phaseBounds,
      courtGameTypeHint,
      partnershipPoolPlayerIds,
      fullWaitlistPlayerIds,
    );
  }

  const varietyMetrics = shapingResult
    ? {
      sessionSaturationScore: shapingResult.saturation.score,
      pairSaturation: shapingResult.saturation.pairSaturation,
      repeatQuartetRate: shapingResult.saturation.repeatQuartetRate,
      shapingApplied: shapingResult.shapingApplied,
      shapingReason: shapingResult.shapingReason,
      topUnitsByVarietyGain: shapingResult.unitRanks.slice(0, 5),
    }
    : scoringFirst && sessionStateForEval?.varietySaturation
      ? {
        sessionSaturationScore: sessionStateForEval.varietySaturation.score,
        pairSaturation: sessionStateForEval.varietySaturation.pairSaturation,
        repeatQuartetRate: sessionStateForEval.varietySaturation.repeatQuartetRate,
        shapingApplied: false,
        shapingReason: 'scoring-first saturation in normalized scoring',
      }
      : null;

  return { relaxationResult, poolRelaxationDetails, varietyMetrics, eligiblePoolResult };
}

/**
 * Greedy selection of high-priority units
 * Works with units instead of individual players
 * @param {Object[]} eligiblePool - Array of units (partnership units, single units, scheduled units)
 * @param {Object} playersById - Map of player objects by ID
 * @param {number} gamesAverage - Average games played
 * @param {Object} playerWaitMetrics - Wait metrics for players
 * @param {Array} partnerships - Array of partnership objects (for reference, not used directly)
 * @param {number|null} gamesMin - Minimum games threshold
 * @param {number} [playersPerMatch=4] - Players needed for one game (2 singles, 4 doubles)
 * @returns {Object[]} Array of selected units
 */
function greedySelectHighPriority(eligiblePool, playersById, gamesAverage, playerWaitMetrics, partnerships, gamesMin = null, playersPerMatch = 4) {
  const ppm = Math.max(2, playersPerMatch);
  const greedySelected = [];
  const processedUnits = new Set();

  // Calculate min games from units
  const minGames = gamesMin !== null ? gamesMin : Math.min(
    ...eligiblePool.map((unit) => getUnitAverageGames(unit, playersById)),
  );

  // First pass: select fairness units at session minimum (average games for partnerships).
  eligiblePool.forEach((unit) => {
    if (processedUnits.has(unit.id)) return;

    const unitAvg = getUnitAverageGames(unit, playersById);
    if (Math.abs(unitAvg - minGames) < 1e-6) {
      greedySelected.push(unit);
      processedUnits.add(unit.id);
    }
  });

  // Second pass: If we don't have enough players for one game, select units behind session min.
  const totalSelected = getTotalPlayerCount(greedySelected);
  if (totalSelected < ppm) {
    eligiblePool.forEach((unit) => {
      if (processedUnits.has(unit.id)) return;

      const unitAvg = getUnitAverageGames(unit, playersById);
      if (unitAvg < minGames) {
        greedySelected.push(unit);
        processedUnits.add(unit.id);
      }
    });
  }

  return greedySelected;
}

function applySoftRandomization(sessionState, gameCount) {
  if (gameCount % 4 === 0 && gameCount > 0) {
    // Use injected seeded PRNG when available (server/EF path); fall back to Math.random (client path).
    const prng = sessionState._prng ?? Math.random;
    sessionState.randomizationActive = true;
    sessionState.randomizationSeed = prng();
    debugLog(`🎲 Soft randomization activated at game ${gameCount}`);
  } else {
    sessionState.randomizationActive = false;
  }
}

/**
 * Singles: pool + greedy + normalized relaxation (same ladder as doubles).
 */
function getBestSinglesCourtAssignmentsNormalized(
  availablePlayers,
  numberOfCourts,
  sessionState,
  playersById,
  partnerships = [],
  playerWaitMetrics = {},
  scheduledGames = [],
  gameTypeRestriction = 'all_types',
  playerCategories = null
) {
  const matchPreferencesEnabled = sessionState?.matchPreferencesEnabled !== false;
  const cooldownEnabled = sessionState?.cooldownEnabled === true;

  const eligiblePlayers = availablePlayers.filter(playerId => {
    const player = playersById[playerId];
    return !player?.scheduled_game_id;
  });

  const partnershipMap = new Map();
  if (partnerships && partnerships.length > 0) {
    partnerships.forEach(p => {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    });
  }

  const eligiblePlayersSet = new Set(eligiblePlayers);
  const playersWithAvailablePartners = eligiblePlayers.filter(playerId => {
    const partnerId = partnershipMap.get(playerId);
    if (!partnerId) {
      return true;
    }
    return eligiblePlayersSet.has(partnerId);
  });

  const filteredPlayersSet = new Set(playersWithAvailablePartners);
  const completePartnerships = partnerships.filter(p => {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    return p1 && p2 && filteredPlayersSet.has(p1) && filteredPlayersSet.has(p2);
  });

  const courts = [];
  let remainingPlayers = [...playersWithAvailablePartners];

  if (sessionState.totalGamesCompleted !== undefined) {
    applySoftRandomization(sessionState, sessionState.totalGamesCompleted);
  }

  for (let courtIdx = 0; courtIdx < numberOfCourts; courtIdx++) {
    if (remainingPlayers.length < 2) {
      debugLog('⚠️ Not enough remaining players for singles court', courtIdx);
      break;
    }

    const eligiblePoolResult = buildEligiblePool(
      remainingPlayers,
      playersById,
      completePartnerships,
      scheduledGames,
      null,
      playerWaitMetrics,
      false,
      gameTypeRestriction,
      numberOfCourts,
      2,
      matchPreferencesEnabled,
      cooldownEnabled,
      sessionState,
    );

    const {
      pool: eligiblePoolUnits,
      minGames: sessionMinGames,
      relaxationDetails: poolRelaxationDetails = [],
    } = eligiblePoolResult;

    if (getTotalPlayerCount(eligiblePoolUnits) < 2) {
      console.warn('⚠️ Not enough eligible players for singles court', courtIdx);
      break;
    }

    const allPlayerIds = eligiblePoolUnits.flatMap(unit => getUnitPlayers(unit));
    const gamesPlayed = allPlayerIds.map(id => {
      const player = playersById[id];
      return player?.effectiveGamesPlayed !== undefined
        ? player.effectiveGamesPlayed
        : (player?.gamesPlayedThisSession || 0);
    });
    const gamesAverage = gamesPlayed.length > 0
      ? gamesPlayed.reduce((sum, g) => sum + g, 0) / gamesPlayed.length
      : 0;

    const greedySelectedUnits = greedySelectHighPriority(
      eligiblePoolUnits,
      playersById,
      gamesAverage,
      playerWaitMetrics,
      completePartnerships,
      sessionMinGames,
      2
    );

    const greedyPlayerIds = greedySelectedUnits.flatMap(unit => getUnitPlayers(unit));

    const relaxationResult = evaluateWithNormalizedRelaxationSingles(
      eligiblePoolResult,
      playersById,
      completePartnerships,
      sessionState,
      greedyPlayerIds,
      scheduledGames,
      gameTypeRestriction,
      playerCategories,
      remainingPlayers
    );

    let playersForPairing = relaxationResult.result;

    if (!playersForPairing && greedyPlayerIds.length < eligiblePoolResult.pool.length) {
      const allEligiblePlayerIds = expandUnitsToPlayers(eligiblePoolResult.pool);
      const fullPoolResult = evaluateWithNormalizedRelaxationSingles(
        eligiblePoolResult,
        playersById,
        completePartnerships,
        sessionState,
        allEligiblePlayerIds,
        scheduledGames,
        gameTypeRestriction,
        playerCategories,
        remainingPlayers
      );
      playersForPairing = fullPoolResult.result;
    }

    if (!playersForPairing && !eligiblePoolResult.cooldownRelaxed) {
      const relaxedPoolResult = buildEligiblePool(
        remainingPlayers,
        playersById,
        completePartnerships,
        scheduledGames,
        null,
        playerWaitMetrics,
        true,
        gameTypeRestriction,
        numberOfCourts,
        2,
        matchPreferencesEnabled,
        cooldownEnabled,
        sessionState,
      );

      if (getTotalPlayerCount(relaxedPoolResult.pool) >= 2) {
        const relaxedGreedySelectedUnits = greedySelectHighPriority(
          relaxedPoolResult.pool,
          playersById,
          gamesAverage,
          playerWaitMetrics,
          completePartnerships,
          relaxedPoolResult.minGames,
          2
        );
        const relaxedGreedyPlayerIds = relaxedGreedySelectedUnits.flatMap(unit => getUnitPlayers(unit));

        let relaxedRelaxationResult = evaluateWithNormalizedRelaxationSingles(
          relaxedPoolResult,
          playersById,
          completePartnerships,
          sessionState,
          relaxedGreedyPlayerIds,
          scheduledGames,
          gameTypeRestriction,
          playerCategories,
          remainingPlayers
        );

        playersForPairing = relaxedRelaxationResult.result;

        if (!playersForPairing && relaxedGreedyPlayerIds.length < relaxedPoolResult.pool.length) {
          const allRelaxedPlayerIds = expandUnitsToPlayers(relaxedPoolResult.pool);
          relaxedRelaxationResult = evaluateWithNormalizedRelaxationSingles(
            relaxedPoolResult,
            playersById,
            completePartnerships,
            sessionState,
            allRelaxedPlayerIds,
            scheduledGames,
            gameTypeRestriction,
            playerCategories,
            remainingPlayers
          );
          playersForPairing = relaxedRelaxationResult.result;
        }

        if (playersForPairing) {
          const allRelaxationDetails = [
            ...(relaxedPoolResult.relaxationDetails || []),
            ...(relaxedRelaxationResult.relaxationDetails || []),
          ];
          courts.push({
            team1: playersForPairing.team1,
            team2: playersForPairing.team2,
            matchmakingRelaxed: (relaxedRelaxationResult.relaxationLevel ?? 0) > 0,
            matchmakingRelaxationDetails:
              allRelaxationDetails.length > 0 ? allRelaxationDetails : undefined,
            matchmakingDebugInfo:
              relaxedRelaxationResult.topCombinations &&
              relaxedRelaxationResult.topCombinations.length > 0
                ? {
                    topCombinations: relaxedRelaxationResult.topCombinations.map(combo => ({
                      team1: combo.team1,
                      team2: combo.team2,
                      team1Names: combo.team1Names,
                      team2Names: combo.team2Names,
                      score: combo.score,
                    })),
                    selectedScore:
                      relaxedRelaxationResult.topCombinations[0]?.score || null,
                    relaxationLevel: relaxedRelaxationResult.relaxationLevel,
                    relaxationName: relaxedRelaxationResult.hardBlocks?.relaxationName,
                  }
                : undefined,
          });
          const assignedIds = [...playersForPairing.team1, ...playersForPairing.team2];
          remainingPlayers = remainingPlayers.filter(id => !assignedIds.includes(id));
          continue;
        }
      }
    }

    if (!playersForPairing) {
      console.warn('⚠️ No valid singles combination for court', courtIdx);
      break;
    }

    const team1 = playersForPairing.team1;
    const team2 = playersForPairing.team2;
    if (!team1 || !team2 || team1.length !== 1 || team2.length !== 1) {
      console.warn('⚠️ Invalid singles pairing for court', courtIdx);
      break;
    }

    const allRelaxationDetails = [
      ...poolRelaxationDetails,
      ...(relaxationResult.relaxationDetails || []),
    ];

    courts.push({
      team1,
      team2,
      matchmakingRelaxed: (relaxationResult.relaxationLevel ?? 0) > 0,
      matchmakingRelaxationDetails: allRelaxationDetails.length > 0 ? allRelaxationDetails : undefined,
      matchmakingDebugInfo:
        relaxationResult.topCombinations && relaxationResult.topCombinations.length > 0
          ? {
              topCombinations: relaxationResult.topCombinations.map(combo => ({
                team1: combo.team1,
                team2: combo.team2,
                team1Names: combo.team1Names,
                team2Names: combo.team2Names,
                score: combo.score,
              })),
              selectedScore: relaxationResult.topCombinations[0]?.score || null,
              relaxationLevel: relaxationResult.relaxationLevel,
              relaxationName: relaxationResult.hardBlocks?.relaxationName,
            }
          : undefined,
    });

    const assignedIds = [...team1, ...team2];
    remainingPlayers = remainingPlayers.filter(id => !assignedIds.includes(id));
  }

  return { courts, waitingList: remainingPlayers };
}

export function getBestCourtAssignmentsTwoStage(
  availablePlayers,
  numberOfCourts,
  sessionState,
  playersById,
  contextAware = false,
  partnerships = [],
  playerWaitMetrics = {},
  scheduledGames = [],
  gameTypeRestriction = 'all_types',
  playerCategories = null,
  matchFormat = 'doubles',
  options = {},
) {
  const fullWaitlistPlayerIds = options.fullWaitlistPlayerIds || null;
  if (isSinglesFormat(matchFormat)) {
    if (MATCHMAKING_CONFIG.featureFlags?.legacySinglesMatchmaking === true) {
      return getBestSinglesCourtAssignments(
        availablePlayers,
        numberOfCourts,
        sessionState,
        playersById,
        contextAware,
        partnerships,
        playerWaitMetrics,
        scheduledGames,
        gameTypeRestriction,
        playerCategories,
        matchFormat
      );
    }
    return getBestSinglesCourtAssignmentsNormalized(
      availablePlayers,
      numberOfCourts,
      sessionState,
      playersById,
      partnerships,
      playerWaitMetrics,
      scheduledGames,
      gameTypeRestriction,
      playerCategories
    );
  }

  const eligiblePlayers = availablePlayers.filter(playerId => {
    const player = playersById[playerId];
    return !player?.scheduled_game_id;
  });

  // Build partnership map to check if players have partners
  const partnershipMap = new Map();
  if (partnerships && partnerships.length > 0) {
    partnerships.forEach(p => {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    });
  }

  // Filter eligible players: EXCLUDE any player whose partner is not in the eligible pool
  // Partners must BOTH be available to play - if one is missing, exclude both
  const filterPlayersWithAvailablePartners = (playerIdList) => {
    const poolSet = new Set(playerIdList);
    return playerIdList.filter((playerId) => {
      const partnerId = partnershipMap.get(playerId);
      if (!partnerId) return true;
      const partnerIsEligible = poolSet.has(partnerId);
      if (!partnerIsEligible) {
        const playerName = playersById[playerId]?.name || playerId;
        const partnerName = playersById[partnerId]?.name || partnerId;
        debugLog(`⏸️ [MATCHMAKING] Excluding ${playerName} from pool - partner ${partnerName} is not available. Will wait until partner is available.`);
        return false;
      }
      return true;
    });
  };

  const playersWithAvailablePartners = filterPlayersWithAvailablePartners(eligiblePlayers);

  const fullWaitlistFiltered = fullWaitlistPlayerIds
    ? filterPlayersWithAvailablePartners(
      fullWaitlistPlayerIds.filter((id) => {
        const player = playersById[id];
        return player && !player?.scheduled_game_id;
      }),
    )
    : null;

  // Now filter partnerships to only include those where BOTH partners are in the filtered pool
  const filteredPlayersSet = new Set(playersWithAvailablePartners);
  const completePartnerships = partnerships.filter(p => {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    return p1 && p2 && filteredPlayersSet.has(p1) && filteredPlayersSet.has(p2);
  });

  debugLog('🎯 Two-stage matchmaking started:', {
    totalPlayers: availablePlayers.length,
    eligiblePlayers: eligiblePlayers.length,
    playersWithAvailablePartners: playersWithAvailablePartners.length,
    excludedDueToMissingPartner: eligiblePlayers.length - playersWithAvailablePartners.length,
    scheduledPlayers: availablePlayers.length - eligiblePlayers.length,
    courts: numberOfCourts,
    contextAware,
    totalPartnerships: partnerships.length,
    completePartnerships: completePartnerships.length,
  });

  const matchPreferencesEnabled = sessionState?.matchPreferencesEnabled !== false;
  const cooldownEnabled = sessionState?.cooldownEnabled === true;

  const courts = [];
  let remainingPlayers = [...playersWithAvailablePartners];

  if (sessionState.totalGamesCompleted !== undefined) {
    applySoftRandomization(sessionState, sessionState.totalGamesCompleted);
  }

  // Compute the game-type budget once per rotation to decide whether the first
  // court slot should be prioritised for an under-target game type.
  const gameTypeBudget = computeGameTypeBudget({
    playersById,
    playerIds: playersWithAvailablePartners,
    numberOfCourts,
    sessionState,
  });
  debugLog('📊 Game-type budget computed:', gameTypeBudget);

  // Split partnerships (partner on court, partner on waitlist) shrink the narrow pool
  // before pool expansion runs. Allow runPoolExpansionRelaxationGate to widen to
  // fullWaitlistFiltered when the full waitlist still has enough players.
  const expandedPoolPlayerCount = fullWaitlistFiltered?.length ?? remainingPlayers.length;

  for (let courtIdx = 0; courtIdx < numberOfCourts; courtIdx++) {
    if (remainingPlayers.length < 4 && expandedPoolPlayerCount < 4) {
      debugLog('⚠️ Not enough remaining players for court', courtIdx);
      break;
    }

    // On the first court slot, set a game-type hint based on which type is most
    // behind its capacity-adjusted target. Falls back gracefully to normal eval
    // if the hinted type cannot be formed from the current pool.
    const courtGameTypeHint = (courtIdx === 0 && gameTypeBudget.priorityGameType)
      ? gameTypeBudget.priorityGameType
      : null;

    if (courtGameTypeHint) {
      debugLog(`👑 Court ${courtIdx} priority hint: ${courtGameTypeHint} (deficit:`, gameTypeBudget.deficit[courtGameTypeHint]?.toFixed(2), ')');
    }

    const courtEvalContext = {
      playersById,
      partnerships,
      scheduledGames,
      playerWaitMetrics,
      gameTypeRestriction,
      numberOfCourts,
      matchPreferencesEnabled,
      sessionState,
      playerCategories,
      courtGameTypeHint,
      fullWaitlistPlayerIds: fullWaitlistFiltered,
    };

    const courtEvalBundle = runPoolExpansionRelaxationGate({
      narrowPlayerIds: remainingPlayers,
      fullWaitlistPlayerIds: fullWaitlistFiltered,
      evaluateForPlayers: (playerIds, { forceRelaxCooldown, phaseBounds }) =>
        evaluateCourtMatchForPlayers(playerIds, {
          ...courtEvalContext,
          forceRelaxCooldown,
          phaseBounds,
          partnershipPoolPlayerIds: fullWaitlistFiltered || remainingPlayers,
          relaxationConfig: sessionState?.relaxationConfig ?? null,
        }),
    });

    if (!courtEvalBundle) {
      console.warn('⚠️ Not enough eligible players for court', courtIdx);
      break;
    }

    const {
      relaxationResult,
      poolRelaxationDetails = [],
      varietyMetrics,
    } = courtEvalBundle;

    const playersForPairing = relaxationResult?.result;

    if (!playersForPairing) {
      console.warn('⚠️ No valid combination found for court', courtIdx, 'after evaluateWithRelaxation');
      break;
    }

    const team1 = playersForPairing.team1;
    const team2 = playersForPairing.team2;
    if (!team1 || !team2 || team1.length !== 2 || team2.length !== 2) {
      console.warn('⚠️ Failed to create pairing for court', courtIdx);
      break;
    }

    const allRelaxationDetails = [
      ...poolRelaxationDetails,
      ...(relaxationResult.relaxationDetails || []),
    ];

    courts.push({
      team1,
      team2,
      matchmakingRelaxed: relaxationResult.relaxationLevel && relaxationResult.relaxationLevel > RELAXATION_LEVELS.STRICT,
      matchmakingRelaxationDetails: allRelaxationDetails.length > 0 ? allRelaxationDetails : undefined,
      matchmakingDebugInfo: relaxationResult.topCombinations && relaxationResult.topCombinations.length > 0
        ? {
            topCombinations: relaxationResult.topCombinations.map(combo => ({
              team1: combo.team1,
              team2: combo.team2,
              team1Names: combo.team1Names,
              team2Names: combo.team2Names,
              score: combo.score,
            })),
            selectedScore: relaxationResult.topCombinations[0]?.score || null,
            relaxationLevel: relaxationResult.relaxationLevel,
            relaxationName: relaxationResult.hardBlocks?.relaxationName,
            varietyMetrics,
            gameTypeBudget: courtIdx === 0 ? gameTypeBudget : undefined,
            courtGameTypeHint: courtGameTypeHint || undefined,
            priorityHintSkipped: courtGameTypeHint
              ? relaxationResult.priorityHintSkipped || undefined
              : undefined,
          }
        : {
            varietyMetrics,
            gameTypeBudget: courtIdx === 0 ? gameTypeBudget : undefined,
            courtGameTypeHint: courtGameTypeHint || undefined,
          },
    });

    const assignedIds = [...team1, ...team2];
    remainingPlayers = remainingPlayers.filter(id => !assignedIds.includes(id));
  }

  debugLog('🏁 Two-stage matchmaking completed:', courts.length, 'courts assigned');

  return {
    courts,
    waitingList: remainingPlayers,
  };
}

export {
  greedySelectHighPriority,
};

